import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { calculateDGBasket, type DGBasketItem, type DGBasketOptions } from "../../utils/toolCalculators";
import { toolInputClass, toolLabelClass } from "./toolUi";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const num = (raw: string) => {
  const value = Number(raw.replace(/[$,\s%]/g, ""));
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

let nextId = 3;
const blankItem = (): DGBasketItem => ({ id: String(nextId++), name: "", price: 0, qty: 1, couponType: "none", couponValue: 0 });

interface Row {
  item: DGBasketItem;
  price: string;
  coupon: string;
}

const STARTER: Row[] = [
  { item: { id: "1", name: "Laundry detergent", price: 11.95, qty: 1, couponType: "amount", couponValue: 3 }, price: "11.95", coupon: "3" },
  { item: { id: "2", name: "Paper towels", price: 7.5, qty: 2, couponType: "none", couponValue: 0 }, price: "7.50", coupon: "" },
];

export default function DGCouponCalculatorTool() {
  const [rows, setRows] = useState<Row[]>(STARTER);
  const [options, setOptions] = useState<DGBasketOptions>({
    thresholdEnabled: true,
    thresholdSpend: 25,
    thresholdOff: 5,
    thresholdBasis: "after",
    taxRate: 0,
    cashBack: 0,
  });
  const [taxRaw, setTaxRaw] = useState("");
  const [cashRaw, setCashRaw] = useState("");
  const result = useMemo(() => calculateDGBasket(rows.map((r) => r.item), options), [rows, options]);
  const lineById = new Map(result.lines.map((l) => [l.id, l]));

  const update = (id: string, patch: Partial<Row> & { itemPatch?: Partial<DGBasketItem> }) =>
    setRows((current) =>
      current.map((r) => (r.item.id === id ? { ...r, ...patch, item: { ...r.item, ...(patch.itemPatch ?? {}) } } : r)),
    );

  return (
    <div>
      <div className="space-y-3">
        {rows.map((row) => {
          const line = lineById.get(row.item.id);
          return (
            <div key={row.item.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="grid grid-cols-6 gap-2 sm:grid-cols-12">
                <input
                  className={`${toolInputClass} col-span-6 sm:col-span-4`}
                  placeholder="Item"
                  value={row.item.name}
                  onChange={(e) => update(row.item.id, { itemPatch: { name: e.target.value } })}
                  aria-label="Item name"
                />
                <input
                  className={`${toolInputClass} col-span-2 sm:col-span-2`}
                  inputMode="decimal"
                  placeholder="Price"
                  value={row.price}
                  onChange={(e) => update(row.item.id, { price: e.target.value, itemPatch: { price: num(e.target.value) } })}
                  aria-label="Price"
                />
                <input
                  className={`${toolInputClass} col-span-1 sm:col-span-1 px-2 text-center`}
                  inputMode="numeric"
                  value={row.item.qty}
                  onChange={(e) => update(row.item.id, { itemPatch: { qty: Math.min(99, Math.floor(num(e.target.value))) } })}
                  aria-label="Quantity"
                />
                <select
                  className={`${toolInputClass} col-span-3 sm:col-span-2 px-2`}
                  value={row.item.couponType}
                  onChange={(e) => update(row.item.id, { itemPatch: { couponType: e.target.value as DGBasketItem["couponType"] } })}
                  aria-label="Coupon type"
                >
                  <option value="none">No coupon</option>
                  <option value="amount">$ off</option>
                  <option value="percent">% off</option>
                </select>
                <input
                  className={`${toolInputClass} col-span-4 sm:col-span-2`}
                  inputMode="decimal"
                  placeholder={row.item.couponType === "percent" ? "% off" : "$ off"}
                  disabled={row.item.couponType === "none"}
                  value={row.item.couponType === "none" ? "" : row.coupon}
                  onChange={(e) => update(row.item.id, { coupon: e.target.value, itemPatch: { couponValue: num(e.target.value) } })}
                  aria-label="Coupon value"
                />
                <button
                  type="button"
                  onClick={() => setRows((current) => current.filter((r) => r.item.id !== row.item.id))}
                  className="col-span-2 sm:col-span-1 flex items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-red-600"
                  aria-label="Remove item"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {line && line.lineTotal > 0 && (
                <p className="mt-2 text-xs text-slate-500">
                  {money(line.lineTotal)}
                  {line.coupon > 0 && <> − {money(line.coupon)} coupon</>} · <strong className="text-slate-700">{money(line.netEach)} each</strong> after all coupons
                </p>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => setRows((current) => [...current, { item: blankItem(), price: "", coupon: "" }])}
          className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-violet-700 hover:border-violet-400"
        >
          <Plus className="h-4 w-4" /> Add item
        </button>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <label className="flex items-center gap-2 font-semibold">
            <input
              type="checkbox"
              checked={options.thresholdEnabled}
              onChange={(e) => setOptions({ ...options, thresholdEnabled: e.target.checked })}
              className="h-4 w-4 accent-violet-700"
            />
            Spend-threshold coupon
          </label>
          {options.thresholdEnabled && (
            <>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span>$</span>
                <input className={`${toolInputClass} w-20 px-2`} inputMode="decimal" value={options.thresholdOff} onChange={(e) => setOptions({ ...options, thresholdOff: num(e.target.value) })} aria-label="Dollars off" />
                <span>off</span>
                <span>$</span>
                <input className={`${toolInputClass} w-20 px-2`} inputMode="decimal" value={options.thresholdSpend} onChange={(e) => setOptions({ ...options, thresholdSpend: num(e.target.value) })} aria-label="Spend required" />
              </div>
              <select
                className={`${toolInputClass} mt-2 text-sm`}
                value={options.thresholdBasis}
                onChange={(e) => setOptions({ ...options, thresholdBasis: e.target.value as DGBasketOptions["thresholdBasis"] })}
                aria-label="Threshold basis"
              >
                <option value="after">Spend counts after item coupons (usual)</option>
                <option value="before">Spend counts before item coupons</option>
              </select>
            </>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <label>
            <span className={toolLabelClass}>Sales tax %</span>
            <input className={toolInputClass} inputMode="decimal" placeholder="0" value={taxRaw} onChange={(e) => { setTaxRaw(e.target.value); setOptions({ ...options, taxRate: num(e.target.value) }); }} />
          </label>
          <label>
            <span className={toolLabelClass}>Cash back $</span>
            <input className={toolInputClass} inputMode="decimal" placeholder="0" value={cashRaw} onChange={(e) => { setCashRaw(e.target.value); setOptions({ ...options, cashBack: num(e.target.value) }); }} />
          </label>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-violet-200 bg-white p-4 sm:p-5" role="status">
        {options.thresholdEnabled && (
          result.thresholdApplied ? (
            <p className="mb-3 rounded-lg bg-emerald-50 p-3 font-semibold text-emerald-800">✓ ${options.thresholdOff} off ${options.thresholdSpend} unlocked</p>
          ) : (
            <p className="mb-3 rounded-lg bg-amber-50 p-3 font-semibold text-amber-900">Add {money(result.shortBy)} more to unlock ${options.thresholdOff} off</p>
          )
        )}
        <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
          <dt className="text-slate-600">Subtotal</dt><dd className="text-right">{money(result.subtotal)}</dd>
          <dt className="text-slate-600">Item coupons</dt><dd className="text-right text-emerald-700">−{money(result.itemCoupons)}</dd>
          {result.thresholdApplied && (<><dt className="text-slate-600">Threshold coupon</dt><dd className="text-right text-emerald-700">−{money(result.thresholdDiscount)}</dd></>)}
          {result.tax > 0 && (<><dt className="text-slate-600">Tax</dt><dd className="text-right">{money(result.tax)}</dd></>)}
          <dt className="mt-2 border-t border-slate-200 pt-2 font-bold">Out of pocket</dt><dd className="mt-2 border-t border-slate-200 pt-2 text-right text-xl font-extrabold">{money(result.outOfPocket)}</dd>
          {options.cashBack > 0 && (<><dt className="text-slate-600">After cash back</dt><dd className="text-right font-bold">{money(result.netAfterCashBack)}</dd></>)}
          <dt className="text-slate-600">You save</dt><dd className="text-right font-semibold text-emerald-700">{money(result.totalSavings)}</dd>
        </dl>
        <p className="mt-3 text-xs text-slate-500">Some states tax the pre-coupon price; check your receipt rules.</p>
      </div>
    </div>
  );
}
